import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap } from 'rxjs/operators';
import { LoginLogoutActionTypes } from '../../../auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../global-message';
import { GlobalMessageActions } from '../../../global-message/store/actions';
import { normalizeHttpError, withdrawOn } from '../../../util';
import { AppActions } from '../../../util/store/actions';
import { AddressConnector } from '../../connectors/address-connector';
import { UserActions } from '../actions';
import { DeliveryAddressActionTypes, DeliveryAddressActions, DeliveryAddressesActions } from '../actions/delivery-address.action';

@Injectable()
export class DeliveryAddressEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  loadDeliveryAddresses$: Observable<DeliveryAddressesActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveryAddressActionTypes.LoadDeliveryAddresses),
      switchMap((action: UserActions.LoadDeliveryAddresses) => {
        const params = action.searchParams;
        return this.connector.loadDeliveryAddresses(params, action.userId).pipe(
          map((res) => new UserActions.LoadDeliveryAddressesSuccess(res, params)),
          catchError((error) => of(new UserActions.LoadDeliveryAddressesFail(params, normalizeHttpError(error))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  saveDeliveryAddress$: Observable<DeliveryAddressActions | GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveryAddressActionTypes.SaveDeliveryAddress),
      mergeMap((action: UserActions.SaveDeliveryAddress) => {
        const params = action.searchParams;
        const address = { ...action.address };
        return this.connector.saveDeliveryAddress(address, action.userId).pipe(
          concatMap((res) => {
            const actions: any[] = [new UserActions.SaveDeliveryAddressSuccess(res || address, params, !address.id)];
            if (action.tempCode) {
              actions.push(new UserActions.UpdateTempDeliveryAddress(res, action.tempCode));
            }
            return actions;
          }),
          catchError((error) => of(new UserActions.SaveDeliveryAddressFail(normalizeHttpError(error))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForSaveDeliveryAddress$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveryAddressActionTypes.SaveDeliveryAddressFail),
      map((action: UserActions.SaveDeliveryAddressFail) => {
        if (!isErrorGloballyHandled(action.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'deliveryAddresses.saveShoppingListFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  deleteDeliveryAddress$: Observable<DeliveryAddressActions | GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveryAddressActionTypes.DeleteDeliveryAddress),
      switchMap((action: UserActions.DeleteDeliveryAddress) => {
        const id = [].concat(action.id).join();
        return this.connector.deleteDeliveryAddress(id, action.userId).pipe(
          map(() => new UserActions.DeleteDeliveryAddressSuccess(id, action.searchParams)),
          catchError((error) => of(new UserActions.DeleteDeliveryAddressFail(normalizeHttpError(error))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForDeleteDeliveryAddress$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveryAddressActionTypes.DeleteDeliveryAddressFail),
      map((action: UserActions.DeleteDeliveryAddressFail) => {
        if (!isErrorGloballyHandled(action.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'deliveryAddresses.removeShoppingListFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  updateTempDeliveryAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveryAddressActionTypes.UpdateTempDeliveryAddress),
      map((action: UserActions.UpdateTempDeliveryAddress) => {
        return new UserActions.RemoveDeliveryAddress(action.tempCode);
      }),
      withdrawOn(this.contextChange$)
    )
  );

  constructor(private actions$: Actions, private connector: AddressConnector) {}
}
